/**
 * PostHog Analytics Events for Client Communications Tab
 * Tracks: Tab usage, messaging, calls, emails, and user interactions
 */

// Event names
export const CLIENT_COMMS_EVENTS = {
  // Tab/View events
  TAB_VIEWED: 'client_comms_tab_viewed',
  TAB_CHANGED: 'client_comms_tab_changed',

  // SMS events
  SMS_SENT: 'client_comms_sms_sent',
  SMS_SEND_FAILED: 'client_comms_sms_send_failed',
  SMS_RETRY_CLICKED: 'client_comms_sms_retry_clicked',

  // Email events
  EMAIL_COMPOSE_OPENED: 'client_comms_email_compose_opened',
  EMAIL_SENT: 'client_comms_email_sent',

  // Call events
  CALL_INITIATED: 'client_comms_call_initiated',

  // Communication interactions
  COMMUNICATION_SELECTED: 'client_comms_communication_selected',
  COMMUNICATION_BOOKMARKED: 'client_comms_communication_bookmarked',
  COMMUNICATION_UNBOOKMARKED: 'client_comms_communication_unbookmarked',

  // Search events
  SEARCH_PERFORMED: 'client_comms_search_performed',

  // Attachment events
  ATTACHMENT_DOWNLOADED: 'client_comms_attachment_downloaded',
  BULK_DOWNLOAD_CLICKED: 'client_comms_bulk_download_clicked',

  // Contact info events
  PHONE_NUMBER_ADDED: 'client_comms_phone_number_added',
  EMAIL_ADDED: 'client_comms_email_added',

  // Transcript events
  CALL_TRANSCRIPT_VIEWED: 'client_comms_call_transcript_viewed',
  CALL_TRANSCRIPT_DOWNLOADED: 'client_comms_call_transcript_downloaded',
  CALL_AUDIO_PLAYED: 'client_comms_call_audio_played',

  // DNC (Do Not Contact) events
  DNC_CONTACT_BLOCKED: 'client_comms_dnc_contact_blocked',
} as const;

// Property names
export const CLIENT_COMMS_PROPERTIES = {
  // Common
  MATTER_ID: 'matter_id',
  PAGE: 'page',
  COMPONENT: 'component',

  // Tab properties
  TAB_NAME: 'tab_name', // 'all' | 'texts' | 'calls' | 'emails' | 'attachments'
  PREVIOUS_TAB: 'previous_tab',

  // Communication properties
  COMMUNICATION_ID: 'communication_id',
  COMMUNICATION_TYPE: 'communication_type', // 'sms' | 'email' | 'phone_call'
  DIRECTION: 'direction', // 'inbound' | 'outbound'

  // SMS properties
  MESSAGE_LENGTH: 'message_length', // character count (not content)

  // Stats properties
  TOTAL_COMMUNICATIONS: 'total_communications',
  SMS_COUNT: 'sms_count',
  EMAIL_COUNT: 'email_count',
  CALL_COUNT: 'call_count',

  // Search properties
  HAS_SEARCH_QUERY: 'has_search_query',
  SEARCH_RESULTS_COUNT: 'search_results_count',

  // Attachment properties
  ATTACHMENT_COUNT: 'attachment_count',
  FILE_TYPE: 'file_type',

  // Bookmark properties
  BOOKMARKED_COUNT: 'bookmarked_count',

  // Error properties
  ERROR_MESSAGE: 'error_message',

  // Call properties
  CALL_DURATION_SECONDS: 'call_duration_seconds',
  HAS_TRANSCRIPT: 'has_transcript',

  // DNC properties
  DO_NOT_CONTACT: 'do_not_contact',
  ACTION_BLOCKED: 'action_blocked', // 'sms' | 'email' | 'call'
} as const;

// Type helpers
export type ClientCommsEvent =
  (typeof CLIENT_COMMS_EVENTS)[keyof typeof CLIENT_COMMS_EVENTS];
export type ClientCommsProperty =
  (typeof CLIENT_COMMS_PROPERTIES)[keyof typeof CLIENT_COMMS_PROPERTIES];
